import { useEffect ,useContext} from 'react';
import { useTheme } from '@emotion/react';
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage, MoreVertRounded } from '@mui/icons-material';
import { IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TablePagination, TableRow, Menu, MenuItem } from '@mui/material';
import { Box } from '@mui/system';
import React, { useState } from 'react'

import CustomButton from '../../common/CustomButton';
import DrawerContext from '../../context/DrawerContext';
import ModalContext from '../../context/ModalContext';
import AuthContext from '../../context/AuthContext';
import PageSearchBar from '../../common/PageSearchBar';
import Fade from '@mui/material/Fade';

function TablePaginationActions(props) {
  const [loading, setLoading] = useState(false)
  const [totalRows, setTotalRows] = useState(0);
  const [tableData, setTableData] = useState([])
  const [selected, setSelected] = useState([])
  
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;
  

  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === 'rtl' ? <LastPage /> : <FirstPage />}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === 'rtl' ? <FirstPage /> : <LastPage />}
      </IconButton>
    </Box>
  );
}

export default function CustomPaginationActionsTable() {

  const modalCtx = useContext(ModalContext);
  const drawerCtx = useContext(DrawerContext);
  const authCtx = useContext(AuthContext);

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(100);
  const [data,setData]=useState([]);  
  const [searchInput, setSearchInput] = useState('');
  const [anchorElFilter, setAnchorElFilter] = React.useState(null);
  const [anchorEl, setAnchorEl] = useState(null); // State to manage the menu anchor
  const [selectedRow, setSelectedRow] = useState(null); // State to track which row is selected
  const [filterValue, setFilterValue] = useState('');
  const [displayedData, setDisplayedData] = useState(data);

  const generateTempLogo = (name) => {
    let initials = "";
    
    if (!name || name.trim().toUpperCase() === "N/A") {
      initials = "N/A";
    } else {
      initials = name.split(" ").map((word) => word.charAt(0).toUpperCase()).slice(0, 2).join("");
    }
  
    return (
      <div
        style={{
          height: "32px",
          width: "32px",
          borderRadius: "100%",
          backgroundColor: "#576574",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          fontSize: "12px",
          fontWeight: "bold",
        }}
      >
        {initials}
      </div>
    );
  };

  const openUploadAgentLogoModal = (data) => {
    modalCtx.setDetails('upload-agent-logo', { data });
    modalCtx.openModal();
  }

  // Handle menu opening
  const open = Boolean(anchorEl);
  const  handleClick = (event,data) => {
    setAnchorEl(event.target);
    setSelectedRow(data);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDeleteClick = (row) => {
    modalCtx.setDetails('delete-agent-modal', { 
      agent: row
    });
    modalCtx.openModal();
  };

  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URI}/v3/admin/agents`, {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${authCtx.token}`
      }
    })
      .then(response => response.json())
      .then(data => setData(data.data));
  }, [drawerCtx, authCtx.token]);

  useEffect(() => {
    if (modalCtx.pageUpdate?.page === "Agents") {
      console.log("\n\nPage update requested. Refetching all agents...");
      const agentId = modalCtx.pageUpdate?.agentId;
      if (agentId) {
        console.log(`Updating agent with ID: ${agentId}`);
      }
      modalCtx.setPageUpdate({ page: "" });
      // Refetch agents data
      fetch(`${process.env.REACT_APP_API_URI}/v3/admin/agents`, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authCtx.token}`
        }
      })
        .then(response => response.json())
        .then(data => setData(data.data));
    } else {
      console.log("No page update requests detected...");
    }
  }, [modalCtx.pageUpdate, authCtx.token]);

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows = page > 0 ? Math.max(0, (1 + page) * rowsPerPage - data.length) : 0;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const showStatus = (status) => {
    if(status === "pending") {
      return <div className='status status-pending'>Pending</div>;
    } else if(status === "invite_sent") {
      return <div className='status status-invite'>Invite Sent</div>;
    } else if(status === "active") {
      return <div className='status status-active'>Active</div>;
    } else if(status === "suspended") {
      return <div className='status status-suspended'>Suspended</div>;
    }
  }

  useEffect(() => {
    const term = (searchInput || '').toLowerCase();
    const filteredData = !term
      ? (data || [])
      : (data || []).filter((item) => {
          const first = (item?.firstName || '').toLowerCase();
          const keywords = (item?.keywords || '').toLowerCase();
          return first.includes(term) || keywords.includes(term);
        });
    setDisplayedData(filteredData);
  }, [filterValue, searchInput, data])

  function toggleDrawerHandler(drawer, user) {
    handleClose();
    drawerCtx.openDrawer();
    drawerCtx.setDetails(drawer, user);
  };

  const handleModal = (modal, data) => {
    handleClose();
    modalCtx.openModal();
    modalCtx.setDetails(modal, data);
  };

  const open2 = Boolean(anchorElFilter);

  const handleCloseFilter = (value) => {
    setAnchorElFilter(null);
    setFilterValue(value)
  };
  
  const handleClickFilter = (event) => {
    setAnchorElFilter(event.currentTarget);
  };

  return (
    <>
      <div className="agents-container">
        <div className='header-section'>
          <span className='title'>AGENTS</span>
          <div className='header-items-div'>
          <PageSearchBar value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search" />
          <div>
            {/* <CustomButton onClick={handleClickFilter} className={'deliva-btn-bordered'} >
              Filter By: {filterValue !== '' ? filterValue : 'All'}
            </CustomButton> */}
            <Menu id="basic-menu" anchorEl={anchorElFilter} open={open2} onClose={() => handleCloseFilter('')}>
              <MenuItem onClick={() => handleCloseFilter('agent')}>Agent</MenuItem>
              <MenuItem onClick={() => handleCloseFilter('')}>All</MenuItem>
            </Menu>
          </div>
          <CustomButton  onClick={() => { handleModal('add-agent-modal', {}) }}>+ Add Agent</CustomButton>
        </div>
      </div>
        <TableContainer component={Paper} className='agent-table'>
          <Table sx={{ minWidth: 500 }}>
          <TableHead className='agent-table-head'>
            <TableRow>
              <TableCell align="left" className="agent-head-cell">Avatar</TableCell>
              <TableCell align="left" className="agent-head-cell">Name & Affiliate Code</TableCell>
              <TableCell align="left" className="agent-head-cell">Phone & Email</TableCell>
              <TableCell align="left" className="agent-head-cell">Status</TableCell>
              <TableCell align="left" className="agent-head-cell">Location</TableCell>
              <TableCell align="right"></TableCell>
            </TableRow>
          </TableHead>
              <TableBody>
                {(
                  rowsPerPage > 0 ? displayedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage) : displayedData).map((data) => (
                    <TableRow key={data.id}>
                      <TableCell style={{ width: '50px', padding: 0, paddingLeft: '1rem' }} align="left" onClick={()=>openUploadAgentLogoModal(data)}>
                        {data.avatar ? (
                          <img style={{ height: "32px", width: "32px", borderRadius: "100%" }} src={data.avatar} alt="Agent Avatar" />
                        ) : (
                          generateTempLogo(`${data.firstName} ${data.lastName}` || "N/A")
                        )}
                      </TableCell>
                      <TableCell style={{ width: 150 }} align="left"><span>{data.firstName} {data.lastName}</span><br/>{data.affiliateCode}</TableCell>
                      <TableCell style={{ width: 120 }} align="left">{data.phone}<br/>{data.email}</TableCell>
                      <TableCell style={{ width: 90 }} align="left">{showStatus(data.status)}</TableCell>
                      <TableCell style={{ width: 160 }} align="left">{data.location}</TableCell>
                      <TableCell style={{ width: 10 }} align="left">
                        <MoreVertRounded aria-controls={open ? 'fade-menu' : undefined} aria-haspopup="true" null aria-expanded={open ? 'true' : undefined} onClick={(e) => { handleClick(e, data); }} />                 
                      </TableCell>
                    </TableRow>
                  )
                )}

                {
                  emptyRows > 0 && (
                    <TableRow style={{ height: 53 * emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )
                }
              </TableBody>
            </Table>
          </TableContainer>
          <Menu id={`fade-menu$`} MenuListProps={{ 'aria-labelledby': `fade-button`, }} anchorEl={anchorEl} open={open} onClose={handleClose} TransitionComponent={Fade} >
            <MenuItem onClick={() => { toggleDrawerHandler('edit-agent', selectedRow); handleClose(); }}>Edit</MenuItem>
            <MenuItem onClick={() => { handleDeleteClick(selectedRow); handleClose(); }}>Delete</MenuItem>
          </Menu>

          <TableContainer component={Paper} className='agent-table-pagination'>
          <Table sx={{ minWidth: 500 }}>
            <TableBody>
              <TableRow>
                <TablePagination
                  rowsPerPageOptions={[ 5, 10, 25, { label: 'All', value: -1 }]}
                  colSpan={3}
                  count={data.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: {
                      'aria-label': 'rows per page',
                    },
                    native: true,
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
}
